import { Component, computed, inject, linkedSignal, signal, TemplateRef, ViewChild } from '@angular/core';
import { BaseDialogComponent, ConfirmDialogComponent, DialogHeaderComponent } from '@cumlaude/shared-components-dialogs';
import { DefaultParallelismPerBron, RInstelling } from '@cumlaude/service-contract';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { Option } from '@cumlaude/shared-utils';
import { firstValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';

export interface WorkflowStartDialogResult {
	bulk: boolean;
	parallelism: number;
}

/**
 * Bepaalt de default parallelism: de per omgeving geconfigureerde default, maar nooit meer pods dan
 * er scholen zijn (er draait één pod per instelling) en minimaal 1. Is er niets geconfigureerd, dan 1.
 */
export function determineDefaultParallelism(aantalScholen: number, geconfigureerd: number | undefined): number {
	return Math.max(1, Math.min(aantalScholen, geconfigureerd ?? 1));
}

@Component({
	selector: 'app-workflow-start-dialog',
	templateUrl: './workflow-start-dialog.component.html',
	styleUrls: ['./workflow-start-dialog.component.scss'],
	imports: [BaseDialogComponent, ButtonComponent, ConfirmDialogComponent, DialogHeaderComponent, SingleSelectDropdownComponent, FormsModule],
})
export class WorkflowStartDialogComponent {
	protected readonly dialogRef = inject<DialogRef<WorkflowStartDialogResult>>(DialogRef);
	protected readonly dialog = inject(Dialog);

	protected readonly data = inject<{
		checkedInstellingen: RInstelling[];
		defaultParallelism?: DefaultParallelismPerBron;
	}>(DIALOG_DATA);

	readonly bulk = signal<boolean | undefined>(undefined);

	bulkOptions: Option<boolean>[] = [new Option(true, 'Bulk verwerking'), new Option(false, 'Delta verwerking')];

	readonly selectedBulkOption = computed(() => {
		return this.bulkOptions.find((option) => option.value === this.bulk());
	});

	readonly aantalScholen = computed(() => this.data.checkedInstellingen.length);

	readonly enabled = computed(() => this.bulk() !== undefined);

	readonly defaultParallelism = computed(() => {
		const geconfigureerd = this.data.defaultParallelism;
		return determineDefaultParallelism(this.aantalScholen(), this.bulk() ? geconfigureerd?.bulk : geconfigureerd?.delta);
	});

	readonly parallelism = linkedSignal<number>(() => this.defaultParallelism());

	@ViewChild('confirmInstellingBulkEnDelta')
	confirmInstellingBulkEnDelta!: TemplateRef<{}>;

	async start() {
		const bulk = this.bulk();
		if (bulk === undefined) return;

		if (!bulk && this.data.checkedInstellingen.some((instelling) => instelling.nextETLBulk)) {
			const confirmDialogRef = this.dialog.open(this.confirmInstellingBulkEnDelta, {});
			const result = await firstValueFrom(confirmDialogRef.closed);
			if (!result) {
				this.dialogRef.close();
				return;
			}
		}

		this.dialogRef.close({ bulk: bulk, parallelism: this.parallelism() });
	}

	close() {
		this.dialogRef.close();
	}
}
